// Shared clinic-config loader — fetched once per (apiBase, clinicSlug) and
// memoized so both the embed shell (FAB branding/locale at page load) and the
// Widget (on open) consume a single network request. A failed fetch clears the
// cache so the next consumer (e.g. reopening the widget) retries.
//
// See SPECS/widget-gen2-velvet-chamber.md ("Theming contract — Injection
// timing") and SPECS/patient-path-localization.md.

import { resolveLocale, type SupportedLocale } from '../i18n';

export interface ClinicConfig {
  brand_name: string;
  logo_url?: string;
  privacy_policy_url?: string;
  booking_url?: string;
  contact_email?: string;
  // Per-clinic patient-path locale (SPECS/patient-path-localization.md). May be
  // absent if the backend has not shipped the field yet — defaults to `en`.
  locale: SupportedLocale;
  colors: {
    primary: string;
    secondary: string;
  };
}

// Velvet Rouge defaults (plum/rose) used when the backend omits colors.
export const DEFAULT_CLINIC_COLORS: ClinicConfig['colors'] = {
  primary: 'oklch(0.32 0.090 5)',
  secondary: 'oklch(0.68 0.120 35)',
};

let cached: { key: string; promise: Promise<ClinicConfig> } | null = null;

async function fetchClinicConfig(apiBase: string, clinicSlug: string): Promise<ClinicConfig> {
  const res = await fetch(
    `${apiBase}/v1/public/clinic-config?clinic_slug=${encodeURIComponent(clinicSlug)}`,
  );

  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json() as { error?: { code?: string; message?: string } };
      if (body.error?.code === 'CLINIC_NOT_FOUND') {
        detail = `Clinic "${clinicSlug}" was not found. Check data-clinic-slug and run db:seed if testing locally.`;
      } else if (body.error?.message) {
        detail = body.error.message;
      }
    } catch {
      /* ignore non-JSON error bodies */
    }
    throw new Error(`Config fetch failed: ${detail}`);
  }

  const data = await res.json() as Record<string, unknown> & {
    colors?: ClinicConfig['colors'];
  };

  return {
    brand_name: data.brand_name as string,
    logo_url: data.logo_url as string | undefined,
    privacy_policy_url: data.privacy_policy_url as string | undefined,
    booking_url: data.booking_url as string | undefined,
    contact_email: data.contact_email as string | undefined,
    locale: resolveLocale(data.locale as string | null | undefined),
    colors: data.colors ?? DEFAULT_CLINIC_COLORS,
  };
}

/**
 * Load clinic config, memoized per (apiBase, clinicSlug). Concurrent and
 * subsequent callers share one in-flight/settled promise; a rejection evicts
 * the cache entry so the next call retries.
 */
export function loadClinicConfig(apiBase: string, clinicSlug: string): Promise<ClinicConfig> {
  const key = `${apiBase}|${clinicSlug}`;
  if (cached?.key === key) {
    return cached.promise;
  }

  const promise = fetchClinicConfig(apiBase, clinicSlug);
  cached = { key, promise };
  promise.catch(() => {
    if (cached?.promise === promise) {
      cached = null;
    }
  });
  return promise;
}

/** Apply the Tier-1 brand variables to the shadow host (idempotent). */
export function applyClinicBranding(shadowHost: HTMLElement, config: ClinicConfig): void {
  shadowHost.style.setProperty('--brand-primary', config.colors.primary);
  shadowHost.style.setProperty('--brand-accent', config.colors.secondary);
}

/** Test-only: clear the memoized entry. */
export function resetClinicConfigCache(): void {
  cached = null;
}
